import { FC, useMemo } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';

import {
  AlgoliaUtil,
  getLocalizedAttributesToRetrieve,
  QueryParamsUtil,
  currentPriceAttribute,
  currentShopifyObjectIdAttribute,
} from '../shared/utils';
import { CurrentRefinements, HideWhenSearchFailed, ResetFilters, SearchErrorMonitor } from '../shared/components';
import { FiltersSection } from './sections';
import { ProductsSection } from '../shared/sections';
import { GraphQLProvider } from '../shared/context/GraphQLProvider';
import { useIsMobile } from '../shared/use/isMobile';
import { CollectionDataLayer } from './sections/CollectionDataLayer';
import { CategoriesTranslationsLoader } from '../shared/translations/CategoriesTranslationsLoader';
import { AlgoliaEtimTranslationLoader } from '../shared/translations/EtimTranslationsLoader';
import { CollectionSearchField } from './components/CollectionSearchField';

const algoliaUtil = new AlgoliaUtil({
  appId: theme.algoliaId,
  apiKey: theme.algoliaApiKey,
  hitsPerPage: {
    products: 50,
    collections: 8,
    articles: 16,
  },
  indexNames: {
    product: theme.algoliaProductIndex,
  },
});

const attributesToRetrieve = [
  'sku',
  'vendor',
  ...getLocalizedAttributesToRetrieve('title', 'handle'),
  'variants_inventory_count',
  currentPriceAttribute,
  'named_tags',
  currentShopifyObjectIdAttribute,
  'lead_time_array',
  'inventory_policy',
  'product_image',
  'UseUp',
  'availability',
  'collections',
  'label_text',
];

const queryParamsUtil = new QueryParamsUtil(window.location.search);

function applyFiltersToCollectionLinks() {
  const collectionLinks: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
    '.collection-list .collection-card-wrapper a, .breadcrumb-nav a'
  );

  // The filters travel to the next collection, which starts at page one.
  const search = new QueryParamsUtil(window.location.search).getPageSearch(1);

  collectionLinks.forEach((link) => {
    link.href = `${link.href.split('?')[0]}${search ? `?${search}` : ''}`;
  });
}

applyFiltersToCollectionLinks();

const initialUIState = queryParamsUtil.getInitialUIState();

export const App: FC = () => {
  const instantSearchParams = algoliaUtil.getInstantSearchParams();
  const algoliaIndexes = algoliaUtil.getIndexes();

  const isMobileOrientation = useIsMobile();

  const collectionFilter = useMemo(() => {
    // Algolia doesn't accept different types of filters in an OR, so we have to "fake" that `UseUp` is a numeric
    // https://support.algolia.com/hc/en-us/articles/12466226974609-Can-I-combine-numeric-and-boolean-filters-with-an-OR-operator
    const filters = ['(variants_inventory_count > 0 OR UseUp = 0)'];

    if (window.collectionPathAlgolia) {
      const path: number[] = window.collectionPathAlgolia;
      const filter = `collections.lvl${path.length - 1}:"${path.join(' > ')}"`;

      filters.push(filter);
    }

    if (window.collectionBrand) {
      filters.push(`vendor:"${window.collectionBrand}"`);
    }

    return filters.join(' AND ');
  }, []);

  return (
    <GraphQLProvider>
      <div className="sr-container">
        {window.collectionBrand && <CategoriesTranslationsLoader loadOnMount />}
        <InstantSearch
          searchClient={instantSearchParams.searchClient}
          indexName={algoliaIndexes.product}
          initialUiState={{
            [algoliaIndexes.product]: initialUIState,
          }}
          routing={false}
          insights={{ insightsInitParams: { userToken: instantSearchParams.userToken } }}
          onStateChange={({ uiState, setUiState }) => {
            uiState[algoliaIndexes.product].refinementList = {
              ...uiState[algoliaIndexes.product].refinementList,
            };

            const { page, refinementList, range, hierarchicalMenu, query } = uiState[algoliaIndexes.product];

            const { origin, pathname } = window.location;
            const newURL = new URL(origin + pathname);

            if (page) {
              newURL.searchParams.set('page', page.toString());
            }

            if (query) {
              newURL.searchParams.set('q', query);
            } else {
              newURL.searchParams.delete('q');
            }

            // @ts-ignore
            Object.entries(refinementList)
              .filter(
                ([key]) =>
                  !(
                    (window.collectionPathAlgolia && key === 'collections') ||
                    (window.collectionBrand && key === 'vendor')
                  )
              )
              .forEach(([key, val]) => {
                newURL.searchParams.set(key, JSON.stringify(val));
              });

            if (range) {
              // @ts-ignore
              Object.entries(range).forEach(([key, val]) => {
                newURL.searchParams.set(key, val);
              });
            }

            if (window.collectionBrand && hierarchicalMenu) {
              Object.entries(hierarchicalMenu).forEach(([key, val]) => {
                newURL.searchParams.set(key, JSON.stringify(val));
              });
            }

            window.history.replaceState({}, ``, newURL);
            applyFiltersToCollectionLinks();

            setUiState(uiState);
          }}
        >
          <Configure
            hitsPerPage={instantSearchParams.hitsPerPage.products}
            userToken={algoliaUtil.userToken}
            removeWordsIfNoResults="allOptional"
            facets={['*']}
            // Setting the following reduces the size of the initial call,
            // but generates multiple calls to Algolia, defeating the purpose
            // facets={['vendor', 'named_tags.*', currentPriceAttribute]}
            maxValuesPerFacet={1000}
            filters={collectionFilter}
            attributesToRetrieve={attributesToRetrieve}
            restrictHighlightAndSnippetArrays
            attributesToHighlight={[]}
          />

          <SearchErrorMonitor />

          <AlgoliaEtimTranslationLoader />

          {/* Filters are useless while the search is down, so they are hidden with the grid. */}
          <HideWhenSearchFailed>
            {isMobileOrientation ? null : (
              <>
                <CurrentRefinements excludeAttributes={[window.collectionBrand ? 'vendor' : 'collections']} />
                <ResetFilters />
              </>
            )}

            {isMobileOrientation ? (
              <FiltersSection mobile={isMobileOrientation} displayCollection={!!window.collectionBrand} />
            ) : null}
          </HideWhenSearchFailed>

          <div className="sr-content-container">
            <HideWhenSearchFailed>
              {isMobileOrientation ? null : (
                <FiltersSection mobile={isMobileOrientation} displayCollection={!!window.collectionBrand} />
              )}
            </HideWhenSearchFailed>

            <ProductsSection
              algoliaUtil={algoliaUtil}
              mobile={isMobileOrientation}
              searchSlot={CollectionSearchField}
            />
            <CollectionDataLayer algoliaUtil={algoliaUtil} />
          </div>
        </InstantSearch>
      </div>
    </GraphQLProvider>
  );
};
